import { ButtonBase, Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { Button, EmphasizedNumber, IconButton, PointDisplay } from 'utoniq-ui'

import {
  Maybe,
  PurchasablePrepaidPointConnectionFragment,
  PurchasablePrepaidPointDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { IonInformationModal } from '@/components/organisms/functionals'
import { constants } from '@/config'
import { useMessage, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { getPlatform } from '@/core/native/app'

import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '../../functionals/PaymentErrorModal'
import { BottomSheet } from './BottomSheet'
import { CardRegisterModal } from './CardRegisterModal'

export type PointChargeDrawerProps = {
  open: boolean
  point: number
  onClose: () => void
  chargeLoading: boolean
  onPurchase: (plan: PurchasablePrepaidPointDetailFragment) => Promise<void>
  purchasablePrepaidPoints: Maybe<PurchasablePrepaidPointConnectionFragment>
  isCardRegistered: boolean
  pageName?: string
}

/**
 * ポイントを購入できるボトムシート
 */
export const PointChargeDrawer: Organism<PointChargeDrawerProps> = ({
  open,
  point,
  onClose,
  chargeLoading,
  onPurchase,
  purchasablePrepaidPoints,
  isCardRegistered,
  pageName,
}) => {
  const { t } = useTranslation('liveDetail')
  const { showError } = useMessage()
  const { analytics } = useAnalytics()
  const [currentPlan, setCurrentPlan] =
    useState<PurchasablePrepaidPointDetailFragment | null>(null)
  const [openPurchaseConfirm, setOpenPurchaseConfirm] = useState<boolean>(false)
  const [openPurchaseComplete, setOpenPurchaseComplete] =
    useState<boolean>(false)
  const [openCreditCardRegister, setOpenCreditCardRegister] =
    useState<boolean>(false)
  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  const isWeb = getPlatform() === 'web'

  const handlePurchase = useCallback(() => {
    if (!currentPlan) return

    onPurchase(currentPlan)
      .then(() => {
        setOpenPurchaseConfirm(false)
        setOpenPurchaseComplete(true)
      })
      .catch(e => {
        switch (e.graphQLErrors?.[0]?.extensions?.errorCode) {
          /**
           * @description
           * 3Dセキュア認証が必要な場合
           */
          case constants.errorCodes.REQUIRE_3D_SECURE:
            setOpenPurchaseConfirm(false)
            setOpenRequire3DSecureModal(true)
            break
          /**
           * @desciption
           * カードが有効期限切れの場合
           */
          case constants.errorCodes.EXPIRED_CARD:
            setOpenPurchaseConfirm(false)
            setOpenExpiredCardModal(true)
            break
          /**
           * @description
           * 何かしらの決済エラーの場合
           */
          case constants.errorCodes.PAYMENT_ERROR:
            setOpenPurchaseConfirm(false)
            setOpenPaymentErrorModal(true)
            break
          default:
            showError(e)
            break
        }
      })
  }, [currentPlan, onPurchase, showError])

  return (
    <>
      <BottomSheet
        open={open}
        onClose={() => {
          onClose()
          setOpenPurchaseConfirm(false)
          setOpenPurchaseConfirm(false)
        }}
        breakpoints={[0, 0.6, 1]}
        initialBreakpoint={0.6}
      >
        <BaseLayout
          header={{
            title: t('pointCharge'),
            right: (
              <IconButton.Close
                edge="end"
                size="small"
                onClick={() => {
                  onClose()
                  setOpenPurchaseConfirm(false)
                  setOpenPurchaseConfirm(false)
                }}
              />
            ),
            sx: { pt: 0 },
          }}
          safeArea={{ pt: 0 }}
        >
          <Stack sx={{ px: 5 }}>
            <PointDisplayCell point={point} />
            <Stack sx={{ py: 4 }} spacing={4}>
              {purchasablePrepaidPoints?.edges.map(({ node }) => (
                <PointChargeButton
                  key={node.id}
                  prepaidPoint={node}
                  onClick={() => {
                    analytics?.track('click_point', {
                      productId: node.metadata.id,
                      prepaidPointId: node.prepaidPointId,
                      pageName,
                    })

                    /**
                     * webでクレカ未登録の時、クレカ登録画面へ遷移を促すモーダルを開く
                     */
                    if (isWeb && !isCardRegistered) {
                      setOpenCreditCardRegister(true)
                      return
                    }
                    setCurrentPlan(node)
                    setOpenPurchaseConfirm(true)
                  }}
                />
              ))}
              <TextLinkCell />
            </Stack>
          </Stack>
        </BaseLayout>
      </BottomSheet>
      <IonInformationModal
        open={openPurchaseConfirm}
        onClose={() => setOpenPurchaseConfirm(false)}
        title={t('confirmCharge')}
        content={t('confirmChargeDescription', {
          point: currentPlan?.points,
          price: currentPlan?.price,
        })}
        cancelText={t('close')}
        buttons={
          <Button
            sx={{ minWidth: 212 }}
            variant="contained"
            color="primary"
            size="large"
            onClick={() => handlePurchase()}
            loading={chargeLoading}
          >
            {t('toBuy')}
          </Button>
        }
      />
      <IonInformationModal
        open={openPurchaseComplete}
        onClose={() => setOpenPurchaseComplete(false)}
        title={t('purchaseCompleted')}
        content={t('purchaseCompletedDesc', { points: currentPlan?.points })}
        buttons={
          <Button
            sx={{ minWidth: 212, pointerEvents: 'auto' }}
            variant="contained"
            color="primary"
            size="large"
            onClick={event => {
              event.stopPropagation()
              event.preventDefault()
              setOpenPurchaseComplete(false)
            }}
          >
            {t('ok')}
          </Button>
        }
      />
      <CardRegisterModal
        useIon
        open={openCreditCardRegister}
        onClose={() => setOpenCreditCardRegister(false)}
      />

      <ExpiredCardModal
        open={openExpiredCardModal}
        onClose={() => setOpenExpiredCardModal(false)}
      />

      <PaymentErrorModal
        open={openPaymentErrorModal}
        onClose={() => setOpenPaymentErrorModal(false)}
      />

      <Require3DSecureModal
        open={openRequire3DSecureModal}
        onClose={() => setOpenRequire3DSecureModal(false)}
      />
    </>
  )
}

type PointDisplayCellProps = {
  point: number
}
const PointDisplayCell: Cell<PointDisplayCellProps> = ({ point }) => {
  const { t } = useTranslation('liveDetail')
  return (
    <CellFrame sx={{ pt: 2, pb: 3 }}>
      <Stack spacing={1} alignItems="center">
        <Typography variant="h6" color="text.secondary">
          {t('pointBalance')}
        </Typography>
        <PointDisplay size="large" point={point} />
      </Stack>
    </CellFrame>
  )
}

type PointChargeButtonProps = {
  prepaidPoint: PurchasablePrepaidPointDetailFragment
  onClick: () => void
}
const PointChargeButton: Cell<PointChargeButtonProps> = ({
  onClick,
  prepaidPoint,
}) => {
  return (
    <ButtonBase
      sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 5 }}
      onClick={onClick}
    >
      <Stack flex={1} direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={2}>
          <PointDisplay point={prepaidPoint.points} />
          <EmphasizedNumber variant="overline" color="primary.main">
            {prepaidPoint.description}
          </EmphasizedNumber>
        </Stack>
        <EmphasizedNumber variant="h5">¥{prepaidPoint.price}</EmphasizedNumber>
      </Stack>
    </ButtonBase>
  )
}

type TextLinkCellProps = {}
const TextLinkCell: Cell<TextLinkCellProps> = () => {
  const { t } = useTranslation('liveDetail')
  return (
    <CellFrame sx={{ pb: 2 }}>
      <Stack alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {t('cancelPurchaseNote')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}
